import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

interface RelatedPostsProps {
  slugs: string[];
  title?: string;
}

/**
 * Renders the related-posts section at the bottom of a blog article.
 * Reuses the cached `['blogs']` query — filters to the configured slugs
 * and only shows published posts.
 */
export default function RelatedPosts({ slugs, title = 'Related reading' }: RelatedPostsProps) {
  const { data: posts = [] } = useQuery({
    queryKey: ['blogs'],
    queryFn: api.listBlogs,
  });

  const items = useMemo(() => {
    if (!slugs || slugs.length === 0) return [];
    const bySlug = new Map(posts.map((p) => [p.rowKey, p]));
    return slugs
      .map((s) => bySlug.get(s))
      .filter((p): p is NonNullable<typeof p> => !!p && p.status === 'published');
  }, [posts, slugs]);

  if (items.length === 0) return null;

  return (
    <section className="border-t border-slate-100 bg-white py-12">
      <div className="container-page mx-auto max-w-5xl">
        <h2 className="mb-6 font-display text-2xl font-bold text-slate-900">{title}</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {items.map((p) => (
            <Link
              key={p.rowKey}
              to={`/blog/${p.rowKey}`}
              className="card overflow-hidden transition-shadow hover:shadow-lg"
            >
              {p.coverImageUrl && (
                <img src={p.coverImageUrl} alt={p.title} className="aspect-video w-full object-cover" />
              )}
              <div className="p-4">
                <h3 className="font-display text-base font-semibold text-slate-900 line-clamp-2">
                  {p.title}
                </h3>
                {p.excerpt && (
                  <p className="mt-1 line-clamp-2 text-sm text-slate-600">{p.excerpt}</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
